// Microbe v2 — persistence + IDLE ECONOMY. The single source of game math.
//
// Owns: the createSave singleton, the TRAITS genome table, derived production rates, the
// live/offline idle accrual, currency crediting, mass growth, and trait purchase. The world
// (canvas), the genome popup, and the HUD all read/write through here (directly or via GameCtx),
// so no renderer ever touches the save shape.
//
// WHERE THE TWO LAYERS COUPLE. v2 has no dives/timer — one persistent world. Idle ALONE only makes
// energy + biomass (storage-capped by 'membrane'). To level anything you also need xp (earned by
// EATING food, plus a tiny alive-drip credited by the world) and — for the deep `rare` traits —
// enzymes, which trickle in ONLY from rare 'vesicle' pellets you must swim out and engulf. So the
// spine is: idle funds the cheap metabolism traits; active feeding funds xp; scarce vesicles fund
// the rare branch. Mass grows from every bite, sets the organism's radius + camera zoom, and
// creeps toward a division threshold (teased in the HUD; division itself is NOT built yet).

import { createSave } from '../src/index.js';
import type { SaveStore } from '../src/index.js';
import { SAVE_VERSION } from './types.js';
import type {
  MicrobeState,
  Wallet,
  CostKey,
  Cost,
  Genome,
  TraitDef,
  TraitId,
  Rates,
  Catchup,
} from './types.js';

/** localStorage namespace for the whole game. */
export const SAVE_KEY = 'gamekit_microbe';

// ---- economy constants (retuned for the no-dive world) ----
//
// Pacing target: a fresh player who feeds gets a first upgrade in ~30-60s (idle alone floors
// photosynthesis L1 at ~40s; a few bites cover its xp), idle visibly ticks, and rates compound
// hard once the metabolism + motility multiplier stack. Enzymes trickle far slower than v1 dives
// (one per rare vesicle engulfed), so the rare branch stays a genuine active-play reward.

/** Base per-second production at an empty genome. xp/enzymes have NO idle rate — feeding funds them. */
const ENERGY_BASE = 0.5;
const BIOMASS_BASE = 0.25;

/** Storage cap for energy + biomass (xp/enzymes are never capped). Grows with 'membrane'. */
const CAP_BASE = 400;
const CAP_PER_MEMBRANE = 350;

/** Fermentation's instant on-buy energy kick (its dead-end sweetener). */
const FERMENT_BURST = 40;

/** Hard offline window: 8h. Non-compounding, and the storage cap usually bites long before this. */
export const OFFLINE_MAX_S = 8 * 3600;

/** Mass a new cell is born at, and the (teaser) mass that flags division-readiness. */
export const MASS_START = 1;
export const MASS_DIVIDE = 60;

/** Empty wallet — kept explicit so a partial/old save can be healed key-by-key. */
const ZERO_WALLET: Wallet = { energy: 0, biomass: 0, xp: 0, enzymes: 0 };

/** Seed state for a brand-new player. lastSeen 0 => first run, no offline award. */
export const DEFAULTS: MicrobeState = {
  version: SAVE_VERSION,
  wallet: { ...ZERO_WALLET },
  genome: {},
  mass: MASS_START,
  lastSeen: 0,
  soundOn: true,
  stats: { maxMass: MASS_START, totalEaten: 0, born: 0 },
};

/** Persisted store, seeded with DEFAULTS (missing top-level keys backfilled by createSave). */
export const save: SaveStore<MicrobeState> = createSave(SAVE_KEY, DEFAULTS);

// createSave only shallow-merges the TOP level, so a save written by an older/partial build could
// carry a nested wallet/stats object missing newer keys. Refill any absent field so downstream
// readers never hit undefined. (genome is a Partial map read through lvl(), so it needs no heal.)
save.patch({
  wallet: { ...ZERO_WALLET, ...save.value.wallet },
  stats: { ...DEFAULTS.stats, ...save.value.stats },
});

// ---- genome trait table (data-driven; costs + max curves live here) ----
// cost(L) = price to go from level L -> L+1; only the currencies that apply are present.
// rare traits (motility/membrane/o2Tolerance) spend enzymes — the scarce, actively-earned gate.
// fermentation is the flagged dead-end (deadEnd:true).

export const TRAITS: TraitDef[] = [
  {
    id: 'photosynthesis',
    name: 'Photosynthesis',
    icon: '🌿',
    blurb: 'Bask in ambient light for a steady trickle of passive energy.',
    branch: 'metabolism',
    max: 6,
    rare: false,
    deadEnd: false,
    cost: (L): Cost => ({ energy: Math.round(20 * 1.7 ** L), xp: 8 * (L + 1) }),
  },
  {
    id: 'co2Harvesting',
    name: 'CO₂ Harvesting',
    icon: '🫧',
    blurb: 'Fix drifting CO₂ into biomass, banking it even while you rest.',
    branch: 'metabolism',
    max: 6,
    rare: false,
    deadEnd: false,
    cost: (L): Cost => ({ biomass: Math.round(15 * 1.7 ** L), xp: 8 * (L + 1) }),
  },
  {
    id: 'fermentation',
    name: 'Fermentation',
    icon: '🍺',
    blurb: 'Ferment sugars for a fierce early energy rush — plus an instant kick on buy.',
    branch: 'metabolism',
    max: 3,
    rare: false,
    deadEnd: true,
    cost: (L): Cost => ({ biomass: Math.round(30 * 2 ** L) }),
  },
  {
    id: 'motility',
    name: 'Motility',
    icon: '💨',
    blurb: 'Beat your flagella harder — hunt faster, and speed every idle rate.',
    branch: 'motility',
    max: 5,
    rare: true,
    deadEnd: false,
    cost: (L): Cost => ({ enzymes: 2 * (L + 1), energy: Math.round(35 * 1.8 ** L) }),
  },
  {
    id: 'membrane',
    name: 'Membrane',
    icon: '🛡️',
    blurb: 'Thicken your envelope to bank far more energy and biomass before it spills.',
    branch: 'defense',
    max: 6,
    rare: true,
    deadEnd: false,
    cost: (L): Cost => ({ enzymes: 2 * (L + 1), biomass: Math.round(35 * 1.8 ** L) }),
  },
  {
    id: 'o2Tolerance',
    name: 'O₂ Tolerance',
    icon: '🅾️',
    blurb: 'Brave oxygen-rich water for a small aerobic-respiration energy trickle.',
    branch: 'defense',
    max: 3,
    rare: true,
    deadEnd: false,
    cost: (L): Cost => ({ enzymes: 4 * (L + 1), xp: 30 * (L + 1) }),
  },
];

export const TRAIT_BY_ID: Record<TraitId, TraitDef> = TRAITS.reduce(
  (acc, t) => {
    acc[t.id] = t;
    return acc;
  },
  {} as Record<TraitId, TraitDef>,
);

// ---- small helpers ----

function clamp(n: number, lo: number, hi: number): number {
  return n < lo ? lo : n > hi ? hi : n;
}

/** Current genome level of `id`; an absent key reads level 0. */
export function lvl(id: TraitId): number {
  return save.value.genome[id] ?? 0;
}

// ---- derived production (recomputed on any genome change) ----

/**
 * Per-second energy + biomass production and the storage cap, from a genome. Metabolism traits
 * add flat rate, motility multiplies ALL idle rates (its idle payoff on top of world speed), and
 * membrane raises the shared energy/biomass cap.
 */
export function rates(s: Readonly<MicrobeState> = save.value): Rates {
  const g = s.genome;
  const photo = g.photosynthesis ?? 0;
  const co2 = g.co2Harvesting ?? 0;
  const ferm = g.fermentation ?? 0;
  const o2 = g.o2Tolerance ?? 0;
  const mot = g.motility ?? 0;
  const mem = g.membrane ?? 0;

  const motMult = 1 + 0.06 * mot;
  const energy = (ENERGY_BASE + 0.6 * photo + 1.2 * ferm + 0.15 * o2) * motMult;
  const biomass = (BIOMASS_BASE + 0.35 * co2) * motMult;
  const cap = CAP_BASE + CAP_PER_MEMBRANE * mem;
  return { energy, biomass, cap };
}

// ---- idle accrual (live foreground tick + offline boot catch-up share one formula) ----

/** Advance energy/biomass by `dt` seconds at current rates, clamped up to the storage cap. */
export function applyProduction(dt: number): void {
  if (!(dt > 0)) return;
  const s = save.value;
  const r = rates(s);
  const w = s.wallet;
  save.patch({
    wallet: {
      ...w,
      energy: Math.min(w.energy + r.energy * dt, r.cap),
      biomass: Math.min(w.biomass + r.biomass * dt, r.cap),
    },
  });
}

/**
 * Add currency. energy + biomass are clamped to [0, cap] (feeding bumps land here alongside idle);
 * xp + enzymes are floored at 0 but otherwise uncapped. Negative deltas are allowed (a spend that
 * doesn't go through buyTrait), which is why the low clamp exists.
 */
export function credit(delta: Partial<Wallet>): void {
  const s = save.value;
  const cap = rates(s).cap;
  const w = s.wallet;
  save.patch({
    wallet: {
      energy: clamp(w.energy + (delta.energy ?? 0), 0, cap),
      biomass: clamp(w.biomass + (delta.biomass ?? 0), 0, cap),
      xp: Math.max(0, w.xp + (delta.xp ?? 0)),
      enzymes: Math.max(0, w.enzymes + (delta.enzymes ?? 0)),
    },
  });
}

/** Feeding grows mass (never shrinks); tracks lifetime max mass + a running eaten count. */
export function addMass(dm: number): void {
  if (dm === 0) return;
  const s = save.value;
  // Honour negative deltas (the encyst penalty), but never shrink below the starting mass.
  const mass = Math.max(MASS_START, s.mass + dm);
  save.patch({
    mass,
    stats: {
      maxMass: Math.max(s.stats.maxMass, mass),
      totalEaten: s.stats.totalEaten + (dm > 0 ? 1 : 0), // only real bites count as "eaten"
      born: s.stats.born,
    },
  });
}

/**
 * BOOT / return-to-foreground catch-up from the saved lastSeen. Elapsed is clamped to
 * [0, OFFLINE_MAX_S]; rates are held constant across the whole window (NON-compounding), lastSeen
 * is re-stamped so the same span can never be claimed twice, and the amounts that actually landed
 * (post-cap) are returned for a "while you were away" beat. lastSeen falsy => first run, award 0.
 */
export function grantOffline(): Catchup {
  const s = save.value;
  const raw = s.lastSeen ? (Date.now() - s.lastSeen) / 1000 : 0;
  const seconds = clamp(raw, 0, OFFLINE_MAX_S);
  const r = rates(s);
  const w = s.wallet;
  const nextEnergy = Math.min(w.energy + r.energy * seconds, r.cap);
  const nextBiomass = Math.min(w.biomass + r.biomass * seconds, r.cap);
  save.patch({
    wallet: { ...w, energy: nextEnergy, biomass: nextBiomass },
    lastSeen: Date.now(),
  });
  return { energy: nextEnergy - w.energy, biomass: nextBiomass - w.biomass, seconds };
}

/** Mark the current moment as the last foreground stamp (call on hide / before close). */
export function stampSeen(): void {
  save.patch({ lastSeen: Date.now() });
}

// ---- trait purchase ----

/** Room to grow AND wallet covers the next level's mixed cost. */
export function canBuy(id: TraitId): boolean {
  const def = TRAIT_BY_ID[id];
  const L = lvl(id);
  if (L >= def.max) return false;
  const cost = def.cost(L);
  const w = save.value.wallet;
  return (Object.keys(cost) as CostKey[]).every((k) => w[k] >= (cost[k] ?? 0));
}

/**
 * Buy one level of `id` if affordable: deduct the mixed cost, bump the genome level, persist.
 * Fermentation additionally grants an instant FERMENT_BURST energy kick (clamped to the new cap).
 * Returns false (no state change) if maxed or unaffordable.
 */
export function buyTrait(id: TraitId): boolean {
  if (!canBuy(id)) return false;
  const s = save.value;
  const def = TRAIT_BY_ID[id];
  const L = lvl(id);
  const cost = def.cost(L);
  const w = s.wallet;
  const genome: Genome = { ...s.genome, [id]: L + 1 };

  const nextWallet: Wallet = {
    energy: w.energy - (cost.energy ?? 0),
    biomass: w.biomass - (cost.biomass ?? 0),
    xp: w.xp - (cost.xp ?? 0),
    enzymes: w.enzymes - (cost.enzymes ?? 0),
  };

  if (id === 'fermentation') {
    const cap = rates({ ...s, genome }).cap;
    nextWallet.energy = Math.min(nextWallet.energy + FERMENT_BURST, cap);
  }

  save.patch({ wallet: nextWallet, genome });
  return true;
}

/** 0..1 division-readiness for the HUD teaser (division itself is NOT built). */
export function divideProgress(): number {
  return clamp(save.value.mass / MASS_DIVIDE, 0, 1);
}
